import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { APIProvider } from '@vis.gl/react-google-maps';
import AddressAutocomplete from '../components/clients/AddressAutocomplete';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Switch } from '../components/ui/switch';
import { DateInput } from '../components/ui/date-input';
import { toast } from 'sonner';
import { calcolaCodiceFiscale } from '../utils/fiscalCode';
import {
  Plus, Search, MoreHorizontal, FileSignature, Download, Upload, Trash2, Pencil,
  FileText, Mail, ChevronLeft, ChevronRight, Sparkles, Loader2, UserPlus, Calculator, Files,
  ArrowUp, ArrowDown,
} from 'lucide-react';
import { emptyClient, NAZIONI } from '../constants/clientConstants';
import ClientCompletionBanner from '../components/clients/ClientCompletionBanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Mappa il tipo documento scelto nel form sul document_type richiesto da /api/ocr/extract
const OCR_DOC_TYPE_MAP = {
  "Carta d'identità elettronica": 'carta_identita',
  "Carta d'identità cartacea": 'carta_identita',
  'Patente': 'patente',
  'Passaporto': 'passaporto',
  'Permesso di soggiorno': 'permesso_soggiorno',
};

// Etichetta breve con cui il documento caricato viene salvato tra i "Documenti anagrafici"
// del cliente (sezione Documenti di Clients.js), per riconoscerlo a colpo d'occhio.
const DOC_LABEL_MAP = {
  "Carta d'identità elettronica": 'CIE',
  "Carta d'identità cartacea": 'CIE',
  'Patente': 'PAT',
  'Passaporto': 'PASS',
  'Permesso di soggiorno': 'PSE',
};

const TIPO_COMUNICAZIONE_OPTIONS = ['Dimissioni volontarie', 'Risoluzione consensuale', 'Revoca'];
// Operatore del Patronato che firma il Mandato (backend: TEMPLATE_PER_OPERATORE in dimissioni.py,
// un template per operatore con nome e firma già stampati).
const OPERATORE_OPTIONS = [
  { value: 'DING', label: 'Ding Yang Da Wei' },
  { value: 'MICHEL', label: 'Zhang Michel' },
];
const TIPO_CONTRATTO_OPTIONS = ['Lavoro a tempo indeterminato', 'Lavoro a tempo determinato', 'Apprendistato'];
const DOC_TIPO_OPTIONS = [
  "Carta d'identità elettronica",
  'Patente',
  'Passaporto',
  'Permesso di soggiorno',
  'Altro',
];

const tipoComunicazioneColors = {
  'Dimissioni volontarie': 'bg-emerald-100 text-emerald-700',
  'Risoluzione consensuale': 'bg-sky-100 text-sky-700',
  'Revoca': 'bg-amber-100 text-amber-700',
};

const emptyDatore = {
  denominazione: '', cf: '', email: '', pec: '', indirizzo: '', comune: '', cap: '', comune_legale: '',
};

const emptyForm = {
  client_id: '',
  doc_tipo: "Carta d'identità elettronica",
  doc_numero: '',
  doc_ente: '',
  cittadinanza: '',
  datore: { ...emptyDatore },
  data_inizio: '',
  tipo_contratto: 'Lavoro a tempo indeterminato',
  data_decorrenza: '',
  tipo_comunicazione: 'Dimissioni volontarie',
  operatore: 'DING',
  codice_modulo: '',
  data_trasmissione: '',
  notes: '',
};

const Dimissioni = () => {
  const [items, setItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [stats, setStats] = useState({ total: 0, dimissioni_volontarie: 0, risoluzione_consensuale: 0, revoca: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(() => new URLSearchParams(window.location.search).get('cerca') || ''); // ?cerca= dal riepilogo cliente
  const [sortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 30;

  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploadingDelegaFor, setUploadingDelegaFor] = useState(null);
  const [extractingDoc, setExtractingDoc] = useState(false);
  const [extractingModulo, setExtractingModulo] = useState(false);
  const [newClientMode, setNewClientMode] = useState(false);
  const [newClientData, setNewClientData] = useState(emptyClient);
  const [creatingClient, setCreatingClient] = useState(false);
  // Documento d'identità caricato mentre il cliente non esiste ancora: va allegato in
  // anagrafica solo DOPO la creazione del cliente al salvataggio (vedi handleSubmit).
  const [pendingIdentityDoc, setPendingIdentityDoc] = useState(null); // { file, label }
  // Ricerca datore di lavoro già registrato (rubrica costruita in automatico dalle pratiche
  // precedenti): permette di precompilare denominazione/CF/indirizzo/PEC con un click quando
  // non si carica un Modulo Recesso (che li compilerebbe già da solo).
  const [datoreSearchTerm, setDatoreSearchTerm] = useState('');
  const [datoreSearchResults, setDatoreSearchResults] = useState([]);
  const [showDatoreDropdown, setShowDatoreDropdown] = useState(false);
  const datoreSearchTimeout = useRef(null);
  // Cliente abbinato per CF da un documento caricato, ma con anagrafica incompleta — mostra
  // il banner "Completa ora" (ClientCompletionBanner) finché non viene completata o si cambia
  // pratica. Nullo quando non c'è nulla da segnalare.
  const [incompleteClient, setIncompleteClient] = useState(null);

  useEffect(() => { fetchClients(); fetchStats(); }, []);

  useEffect(() => { setCurrentPage(1); }, [searchTerm]);

  // Nome del lavoratore nel campo di ricerca anche quando viene abbinato da un documento
  // caricato (documento d'identità, Modulo Recesso) o si apre una pratica esistente
  useEffect(() => {
    const c = clients.find(x => x.id === formData.client_id);
    if (c) setClientSearch(`${c.last_name} ${c.first_name}`);
  }, [formData.client_id, clients]);

  useEffect(() => {
    const timer = setTimeout(() => { fetchItems(); }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, currentPage, sortDirection]);

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API}/clients`);
      setClients(res.data);
    } catch (e) {
      toast.error('Errore nel caricamento dei clienti');
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/dimissioni/stats`);
      setStats(res.data);
    } catch (e) {
      // silenzioso
    }
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      params.append('page', currentPage);
      params.append('page_size', ITEMS_PER_PAGE);
      params.append('sort_field', sortField);
      params.append('sort_direction', sortDirection);
      const res = await axios.get(`${API}/dimissioni?${params.toString()}`);
      setItems(res.data.items);
      setTotalItems(res.data.total);
    } catch (e) {
      toast.error('Errore nel caricamento delle pratiche');
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = clients.filter(c => {
    if (!clientSearch) return true;
    const q = clientSearch.toLowerCase();
    const cognomeNome = `${c.last_name} ${c.first_name}`.toLowerCase();
    const nomeCognome = `${c.first_name} ${c.last_name}`.toLowerCase();
    return cognomeNome.includes(q) || nomeCognome.includes(q) || (c.fiscal_code || '').toLowerCase().includes(q);
  });

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatDateTime = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const toggleSortDirection = () => {
    setCurrentPage(1);
    setSortDirection(d => (d === 'desc' ? 'asc' : 'desc'));
  };

  const openNew = () => {
    setEditingRecord(null);
    setFormData(emptyForm);
    setClientSearch('');
    setNewClientMode(false);
    setNewClientData(emptyClient);
    setPendingIdentityDoc(null);
    setDatoreSearchTerm('');
    setDatoreSearchResults([]);
    setIncompleteClient(null);
    setDialogOpen(true);
  };

  const openEdit = (record) => {
    setEditingRecord(record);
    setNewClientMode(false);
    setNewClientData(emptyClient);
    setPendingIdentityDoc(null);
    setDatoreSearchTerm(record.datore?.denominazione || '');
    setDatoreSearchResults([]);
    setIncompleteClient(null);
    setFormData({
      client_id: record.client_id || '',
      doc_tipo: record.doc_tipo || "Carta d'identità elettronica",
      doc_numero: record.doc_numero || '',
      doc_ente: record.doc_ente || '',
      cittadinanza: record.cittadinanza || '',
      datore: { ...emptyDatore, ...(record.datore || {}) },
      data_inizio: record.data_inizio || '',
      tipo_contratto: record.tipo_contratto || 'Lavoro a tempo indeterminato',
      data_decorrenza: record.data_decorrenza || '',
      tipo_comunicazione: record.tipo_comunicazione || 'Dimissioni volontarie',
      operatore: record.operatore || 'DING',
      codice_modulo: record.codice_modulo || '',
      data_trasmissione: record.data_trasmissione || '',
      notes: record.notes || '',
    });
    setClientSearch('');
    setDialogOpen(true);
  };

  // Normalizza un valore estratto dall'OCR (spesso in MAIUSCOLO o con testo leggermente
  // diverso) su una delle opzioni esatte del menu a tendina — altrimenti il <Select>
  // non lo riconosce e appare vuoto anche se il dato è stato salvato in formData.
  const matchOption = (value, options) => {
    if (!value) return '';
    const v = value.toLowerCase().trim();
    const exact = options.find(o => o.toLowerCase() === v);
    if (exact) return exact;
    const partial = options.find(o => v.includes(o.toLowerCase()) || o.toLowerCase().includes(v));
    return partial || value;
  };

  // Pulsante "Calcola CF" del form nuovo cliente (stesso di Anagrafica Clienti): calcola il CF
  // dai dati anagrafici e avvisa se appartiene già a un cliente registrato.
  const handleCalcolaCfDaDati = async () => {
    try {
      const cf = await calcolaCodiceFiscale(newClientData);
      setNewClientData(prev => ({ ...prev, fiscal_code: cf }));
      const existing = matchClientByCf(cf);
      if (existing) {
        toast.warning(`Attenzione: CF già registrato in anagrafica (${existing.last_name} ${existing.first_name})`);
      } else {
        toast.success('Codice Fiscale calcolato');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Prova ad abbinare un cliente esistente in anagrafica tramite codice fiscale estratto
  const matchClientByCf = (cf) => {
    if (!cf) return null;
    return clients.find(c => (c.fiscal_code || '').toUpperCase() === cf.toUpperCase()) || null;
  };

  // Allega il file caricato ai "Documenti anagrafici" del cliente (stessa sezione di
  // Clients.js), rinominato come "SIGLA COGNOME NOME" (es. "CIE ROSSI MARIO").
  // Se esiste già un documento dello stesso tipo (stessa sigla) in anagrafica, lo sostituisce
  // invece di aggiungerne un altro — la registrazione di un tipo documento va fatta una sola volta.
  const uploadClientDocument = async (clientId, file, shortLabel, lastName, firstName, expiryDate) => {
    try {
      const label = `${shortLabel} ${lastName || ''} ${firstName || ''}`.replace(/\s+/g, ' ').trim().toUpperCase();
      try {
        const clientRes = await axios.get(`${API}/clients/${clientId}`);
        const existing = (clientRes.data.documents || []).find(
          (d) => d.category === 'anagrafico' && (d.description || '').toUpperCase().startsWith(`${shortLabel} `)
        );
        if (existing) {
          await axios.delete(`${API}/clients/${clientId}/documents/${existing.id}`);
        }
      } catch (_) {
        // non bloccare l'upload se il controllo duplicati fallisce
      }
      const fd = new FormData();
      fd.append('file', file);
      fd.append('description', label);
      fd.append('category', 'anagrafico');
      if (expiryDate) fd.append('expiry_date', expiryDate);
      await axios.post(`${API}/clients/${clientId}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    } catch (error) {
      toast.error(`Dati estratti, ma il documento non è stato allegato in anagrafica: ${error.response?.data?.detail || 'errore'}`);
    }
  };

  const handleExtractIdentityDoc = async (file) => {
    if (!file) return;
    const ocrType = OCR_DOC_TYPE_MAP[formData.doc_tipo];
    if (!ocrType) {
      toast.error('Estrazione automatica non disponibile per "Altro" — seleziona il tipo documento corretto o inserisci i dati a mano');
      return;
    }
    const docLabel = DOC_LABEL_MAP[formData.doc_tipo] || ocrType.toUpperCase();
    setExtractingDoc(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('document_type', ocrType);

      // Estrae SEMPRE senza creare nulla in anagrafica: decidiamo noi in automatico se è un
      // cliente già esistente (abbiniamo per CF) o uno nuovo — in quel caso si passa da soli
      // alla modalità "Registra nuovo cliente" già precompilata, senza che l'operatore debba
      // attivarla a mano prima di caricare il documento.
      const res = await axios.post(`${API}/ocr/extract-client-data`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = res.data.extracted_data || {};
      const c = res.data.client_data || {};
      const numero = d.document_number || d.license_number || '';
      const ente = d.issuing_authority || '';
      const cittad = d.nationality || '';
      const expiryDate = d.expiry_date || '';
      if (!numero && !ente && !cittad && !c.fiscal_code && !c.first_name) {
        toast.error('Nessun dato riconosciuto nel documento — verifica di aver scelto il "Tipo documento" corretto sopra prima di ricaricarlo');
        return;
      }
      setFormData(prev => ({
        ...prev,
        doc_numero: numero || prev.doc_numero,
        doc_ente: ente || prev.doc_ente,
        cittadinanza: cittad || prev.cittadinanza,
      }));

      const match = matchClientByCf(c.fiscal_code);
      if (match) {
        setNewClientMode(false);
        setFormData(prev => ({ ...prev, client_id: match.id }));
        setIncompleteClient(match);
        await uploadClientDocument(match.id, file, docLabel, match.last_name, match.first_name, expiryDate);
        toast.success(`Cliente esistente abbinato: ${match.first_name} ${match.last_name} — documento allegato in anagrafica`);
      } else {
        // Unisce con quanto già nel form: un secondo documento caricato dopo il primo completa
        // i campi mancanti senza perdere quelli già trovati.
        let merged = {
          ...newClientData,
          first_name: c.first_name || newClientData.first_name,
          last_name: c.last_name || newClientData.last_name,
          fiscal_code: c.fiscal_code || newClientData.fiscal_code,
          gender: c.gender || newClientData.gender,
          birth_date: c.birth_date || newClientData.birth_date,
          birth_abroad: c.birth_abroad !== undefined ? c.birth_abroad : newClientData.birth_abroad,
          birth_comune: c.birth_comune || (c.birth_abroad ? '' : newClientData.birth_comune),
          birth_provincia: c.birth_provincia || newClientData.birth_provincia,
          birth_country: c.birth_country || newClientData.birth_country,
          address: c.address || newClientData.address,
          address_number: c.address_number || newClientData.address_number,
          city: c.city || newClientData.city,
          city_provincia: c.city_provincia || newClientData.city_provincia,
        };

        // CAP non riportato sul documento (le CIE italiane non lo stampano nell'indirizzo) —
        // se comune e provincia di residenza sono noti e individuano un solo CAP possibile,
        // lo compiliamo in automatico invece di lasciarlo vuoto.
        if (!merged.postal_code && merged.city && merged.city_provincia) {
          try {
            const capRes = await axios.get(`${API}/cap`, { params: { city: merged.city, provincia: merged.city_provincia } });
            if (capRes.data.single && capRes.data.auto_cap) {
              merged = { ...merged, postal_code: capRes.data.auto_cap };
            }
          } catch (e) {
            // il CAP è un aiuto in più: se il lookup fallisce non deve bloccare il resto del flusso
          }
        }

        // Il Passaporto (e spesso anche Patente/PSE) non riporta il CF italiano — se ora
        // abbiamo tutti i dati per calcolarlo (cognome, nome, sesso, nascita, luogo), lo
        // calcoliamo in automatico invece di lasciarlo vuoto.
        const canCalcCf = !merged.fiscal_code && merged.last_name && merged.first_name && merged.gender && merged.birth_date &&
          ((!merged.birth_abroad && merged.birth_comune && merged.birth_provincia) || (merged.birth_abroad && merged.birth_country));
        if (canCalcCf) {
          try {
            const cfRes = await axios.post(`${API}/calculate-fiscal-code`, {
              surname: merged.last_name,
              name: merged.first_name,
              birth_date: merged.birth_date,
              gender: merged.gender,
              birth_abroad: merged.birth_abroad,
              birth_comune: merged.birth_comune,
              birth_provincia: merged.birth_provincia,
              birth_country: merged.birth_country,
            });
            if (cfRes.data.fiscal_code) merged = { ...merged, fiscal_code: cfRes.data.fiscal_code };
          } catch (e) {
            // il calcolo CF è un aiuto in più: se fallisce non deve bloccare il resto del flusso
          }
        }

        // Il CF appena calcolato potrebbe corrispondere a un cliente già esistente — ricontrolla
        // per evitare di creare un duplicato.
        const matchAfterCalc = canCalcCf ? matchClientByCf(merged.fiscal_code) : null;
        if (matchAfterCalc) {
          setNewClientMode(false);
          setFormData(prev => ({ ...prev, client_id: matchAfterCalc.id }));
          setIncompleteClient(matchAfterCalc);
          await uploadClientDocument(matchAfterCalc.id, file, docLabel, matchAfterCalc.last_name, matchAfterCalc.first_name, expiryDate);
          toast.success(`Cliente esistente abbinato: ${matchAfterCalc.first_name} ${matchAfterCalc.last_name} — documento allegato in anagrafica`);
        } else {
          setNewClientMode(true);
          // Il cliente non esiste ancora: il documento si allega dopo la creazione (in handleSubmit).
          setPendingIdentityDoc({ file, label: docLabel, expiryDate });
          setNewClientData(merged);
          toast.success('Nuovo cliente rilevato — dati precompilati, rivedi e salva (il documento si allega automaticamente)');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'estrazione dati dal documento');
    } finally {
      setExtractingDoc(false);
    }
  };

  const handleExtractModuloRecesso = async (file) => {
    if (!file) return;
    setExtractingModulo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('document_type', 'modulo_recesso');
      const res = await axios.post(`${API}/ocr/extract`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = res.data.extracted_data || {};
      if (!d.lavoratore_cf && !d.datore_denominazione && !d.codice_modulo) {
        toast.error('Nessun dato riconosciuto nel Modulo Recesso caricato — controlla che il file sia leggibile e riprova');
        return;
      }
      const tipoComm = d.tipo_comunicazione
        ? matchOption(d.tipo_comunicazione, TIPO_COMUNICAZIONE_OPTIONS)
        : '';
      const tipoContr = d.tipo_contratto
        ? matchOption(d.tipo_contratto, TIPO_CONTRATTO_OPTIONS)
        : '';
      setFormData(prev => ({
        ...prev,
        datore: {
          denominazione: d.datore_denominazione || prev.datore.denominazione,
          cf: d.datore_cf || prev.datore.cf,
          email: (d.datore_email || prev.datore.email || '').toUpperCase(),
          pec: (d.datore_pec || prev.datore.pec || '').toUpperCase(),
          indirizzo: d.datore_indirizzo || prev.datore.indirizzo,
          comune: d.datore_comune || prev.datore.comune,
          cap: d.datore_cap || prev.datore.cap,
          comune_legale: d.datore_comune_legale || prev.datore.comune_legale,
        },
        data_inizio: d.data_inizio || prev.data_inizio,
        tipo_contratto: tipoContr || prev.tipo_contratto,
        data_decorrenza: d.data_decorrenza || prev.data_decorrenza,
        tipo_comunicazione: tipoComm || prev.tipo_comunicazione,
        codice_modulo: d.codice_modulo || prev.codice_modulo,
        data_trasmissione: d.data_trasmissione || prev.data_trasmissione,
      }));
      if (d.datore_denominazione) setDatoreSearchTerm(d.datore_denominazione);
      // Il CF del lavoratore è "matematico" — se lo troviamo, ha sempre priorità su un
      // eventuale match/non-match già dedotto dal documento d'identità caricato prima.
      const match = matchClientByCf(d.lavoratore_cf);
      if (match) {
        setNewClientMode(false);
        setFormData(prev => ({ ...prev, client_id: match.id }));
        setIncompleteClient(match);
        toast.success(`Dati estratti dal Modulo Recesso — lavoratore abbinato: ${match.first_name} ${match.last_name}`);
      } else {
        if (d.lavoratore_nome || d.lavoratore_cognome || d.lavoratore_cf) {
          // Nessun cliente esistente abbinato: passa/resta in "Registra nuovo cliente" e
          // completa il form coi dati del lavoratore dal Modulo Recesso (nome/cognome/CF) —
          // il CF viene anche decodificato per sesso, data e luogo di nascita.
          setNewClientMode(true);
          setNewClientData(prev => ({
            ...prev,
            first_name: d.lavoratore_nome || prev.first_name,
            last_name: d.lavoratore_cognome || prev.last_name,
            fiscal_code: d.lavoratore_cf || prev.fiscal_code,
            email: (d.lavoratore_email || prev.email || '').toUpperCase(),
            phone: d.lavoratore_cellulare || prev.phone,
          }));
          if (d.lavoratore_cf) {
            await applyCfDecodeToNewClient(d.lavoratore_cf);
          }
        }
        toast.success('Dati estratti dal Modulo Recesso');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'estrazione dati dal modulo');
    } finally {
      setExtractingModulo(false);
    }
  };

  // Ricerca (con debounce) nella rubrica datori di lavoro già registrati — si popola da sola
  // ogni volta che si salva una pratica con denominazione+CF del datore compilati.
  const handleDatoreSearch = (term) => {
    setDatoreSearchTerm(term);
    setShowDatoreDropdown(true);
    if (datoreSearchTimeout.current) clearTimeout(datoreSearchTimeout.current);
    if (!term.trim()) {
      setDatoreSearchResults([]);
      return;
    }
    datoreSearchTimeout.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/dimissioni/datori-lavoro/search`, { params: { q: term } });
        setDatoreSearchResults(res.data || []);
      } catch (error) {
        setDatoreSearchResults([]);
      }
    }, 300);
  };

  const selectDatoreFromRegistry = (datore) => {
    setFormData(prev => ({
      ...prev,
      datore: {
        denominazione: datore.denominazione || '',
        cf: datore.cf || '',
        email: datore.email || '',
        pec: datore.pec || '',
        indirizzo: datore.indirizzo || '',
        comune: datore.comune || '',
        cap: datore.cap || '',
        comune_legale: datore.comune_legale || '',
      },
    }));
    setDatoreSearchTerm(datore.denominazione || '');
    setShowDatoreDropdown(false);
  };

  // Decodifica il CF (sesso, data e — quando riconosciuto — luogo di nascita: comune italiano
  // o nazione estera) e lo applica al form "Registra nuovo cliente". Il CF è "matematico" quindi
  // questi dati hanno priorità su quanto letto (magari in modo impreciso) dal documento OCR.
  const applyCfDecodeToNewClient = async (cfRaw) => {
    const cf = (cfRaw || '').trim().toUpperCase();
    if (cf.length < 16) return false;
    try {
      const res = await axios.post(`${API}/decode-fiscal-code`, { fiscal_code: cf });
      if (!res.data) return false;
      const bd = res.data.birth_date || '';
      const birthDateIso = /^\d{4}-\d{2}-\d{2}$/.test(bd)
        ? bd
        : (() => { const p = bd.split('/'); return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : ''; })();
      setNewClientData(prev => ({
        ...prev,
        gender: res.data.gender || prev.gender,
        birth_date: birthDateIso || prev.birth_date,
        ...(res.data.birth_abroad !== undefined ? {
          birth_abroad: res.data.birth_abroad,
          birth_country: res.data.birth_country || '',
          birth_comune: res.data.birth_comune || '',
          birth_provincia: res.data.birth_provincia || '',
        } : {}),
      }));
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleCalcolaCfNewClient = async () => {
    const cf = (newClientData.fiscal_code || '').trim().toUpperCase();
    if (cf.length < 16) {
      toast.error('Codice fiscale non valido (servono 16 caratteri)');
      return;
    }
    const ok = await applyCfDecodeToNewClient(cf);
    if (ok) {
      toast.success('Sesso, data e luogo di nascita calcolati dal Codice Fiscale');
    } else {
      toast.error('Impossibile calcolare i dati dal Codice Fiscale');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let clientId = formData.client_id;

    if (newClientMode) {
      if (!newClientData.last_name || !newClientData.first_name) {
        toast.error('Compila almeno Cognome e Nome del nuovo cliente');
        return;
      }
      if (!newClientData.birth_abroad && (!newClientData.birth_comune || !newClientData.birth_provincia)) {
        toast.error('Compila Comune e Provincia di nascita (o attiva "Nato/a all\'estero")');
        return;
      }
      if (newClientData.birth_abroad && !newClientData.birth_country) {
        toast.error('Seleziona la Nazione di nascita');
        return;
      }
      setCreatingClient(true);
      try {
        const res = await axios.post(`${API}/clients`, newClientData);
        clientId = res.data.id;
        if (pendingIdentityDoc) {
          await uploadClientDocument(clientId, pendingIdentityDoc.file, pendingIdentityDoc.label, newClientData.last_name, newClientData.first_name, pendingIdentityDoc.expiryDate);
          setPendingIdentityDoc(null);
        }
        toast.success('Nuovo cliente registrato in anagrafica');
        fetchClients();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Errore nella registrazione del nuovo cliente');
        setCreatingClient(false);
        return;
      }
      setCreatingClient(false);
    }

    if (!clientId) {
      toast.error('Seleziona il lavoratore');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...formData, client_id: clientId };
      if (editingRecord) {
        await axios.put(`${API}/dimissioni/${editingRecord.id}`, payload);
        toast.success('Pratica aggiornata');
      } else {
        await axios.post(`${API}/dimissioni`, payload);
        toast.success('Pratica creata');
      }
      setDialogOpen(false);
      fetchItems();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    if (!window.confirm(`Eliminare la pratica di ${record.client_name}?`)) return;
    try {
      await axios.delete(`${API}/dimissioni/${record.id}`);
      toast.success('Pratica eliminata');
      fetchItems();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'eliminazione');
    }
  };

  const handleUploadDelega = async (record, file) => {
    if (!file) return;
    setUploadingDelegaFor(record.id);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/dimissioni/${record.id}/upload-delega`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Delega caricata');
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel caricamento della Delega');
    } finally {
      setUploadingDelegaFor(null);
    }
  };

  const openPdf = async (url, filename) => {
    try {
      const res = await axios.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(blobUrl, '_blank');
    } catch (error) {
      toast.error('Documento non disponibile');
    }
  };

  // Apre Mandato + Consenso + Lettera Dimissioni + (se caricata) Delega come un unico PDF —
  // un solo file invece di aprire più finestre, così non c'è rischio che il popup blocker del
  // browser ne blocchi alcune (bloccherebbe tutte tranne la prima).
  const openAllDocuments = (record) => {
    openPdf(`${API}/dimissioni/${record.id}/tutti-documenti-pdf`, `documenti_dimissioni_${record.id}.pdf`);
  };

  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dimissioni-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Dimissioni
          </h1>
          <p className="text-slate-600 mt-1">Dimissioni Volontarie / Risoluzione Consensuale / Revoca</p>
        </div>
        <Button onClick={openNew} data-testid="new-dimissione-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nuova Pratica
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-slate-500"><CardContent className="p-4">
          <p className="text-sm text-slate-500">Totale</p>
          <p className="text-2xl font-bold">{stats.total}</p>
        </CardContent></Card>
        <Card className="border-l-4 border-l-emerald-500"><CardContent className="p-4">
          <p className="text-sm text-slate-500">Dimissioni Volontarie</p>
          <p className="text-2xl font-bold">{stats.dimissioni_volontarie}</p>
        </CardContent></Card>
        <Card className="border-l-4 border-l-sky-500"><CardContent className="p-4">
          <p className="text-sm text-slate-500">Risoluzione Consensuale</p>
          <p className="text-2xl font-bold">{stats.risoluzione_consensuale}</p>
        </CardContent></Card>
        <Card className="border-l-4 border-l-amber-500"><CardContent className="p-4">
          <p className="text-sm text-slate-500">Revoca</p>
          <p className="text-2xl font-bold">{stats.revoca}</p>
        </CardContent></Card>
      </div>

      {/* Search */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Cerca per lavoratore o datore di lavoro..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-dimissioni"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Caricamento...</div>
          ) : totalItems === 0 ? (
            <div className="p-8 text-center text-slate-500">Nessuna pratica trovata</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Lavoratore</TableHead>
                    <TableHead className="font-semibold">Datore di Lavoro</TableHead>
                    <TableHead className="font-semibold">Tipo Comunicazione</TableHead>
                    <TableHead className="font-semibold">Decorrenza</TableHead>
                    <TableHead className="font-semibold">
                      <button
                        type="button"
                        className="flex items-center gap-1 hover:text-slate-900"
                        onClick={toggleSortDirection}
                        title={sortDirection === 'desc' ? 'Più recenti prima' : 'Meno recenti prima'}
                      >
                        Data Caricamento
                        {sortDirection === 'desc' ? <ArrowDown className="w-3.5 h-3.5" /> : <ArrowUp className="w-3.5 h-3.5" />}
                      </button>
                    </TableHead>
                    <TableHead className="font-semibold">Delega</TableHead>
                    <TableHead className="font-semibold">Documenti</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((record) => (
                    <TableRow key={record.id} data-testid={`dimissione-row-${record.id}`}>
                      <TableCell className="cursor-pointer hover:bg-slate-50" onClick={() => openEdit(record)}>
                        <div className="font-medium text-slate-900">{record.client_name || '-'}</div>
                        <div className="text-xs text-slate-500 font-mono">{record.client_fiscal_code}</div>
                      </TableCell>
                      <TableCell className="text-slate-600 cursor-pointer hover:bg-slate-50" onClick={() => openEdit(record)}>
                        <div>{record.datore?.denominazione || '-'}</div>
                        {record.datore?.cf && (
                          <div className="text-xs text-slate-500 font-mono">{record.datore.cf}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={tipoComunicazioneColors[record.tipo_comunicazione] || 'bg-slate-100 text-slate-700'}>
                          {record.tipo_comunicazione}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-600">{formatDate(record.data_decorrenza)}</TableCell>
                      <TableCell className="text-slate-600 text-sm">{formatDateTime(record.created_at)}</TableCell>
                      <TableCell>
                        {record.delega_filename ? (
                          <Badge className="bg-emerald-100 text-emerald-700">
                            <FileSignature className="w-3 h-3 mr-1" />Caricata
                          </Badge>
                        ) : (
                          <label className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 cursor-pointer border border-dashed border-slate-300 rounded-full px-2.5 py-1">
                            {uploadingDelegaFor === record.id ? (
                              'Caricamento...'
                            ) : (
                              <>
                                <Upload className="w-3 h-3" /> Carica
                              </>
                            )}
                            <input
                              type="file"
                              accept="application/pdf"
                              className="hidden"
                              disabled={uploadingDelegaFor === record.id}
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                handleUploadDelega(record, f);
                                e.target.value = '';
                              }}
                            />
                          </label>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <Button
                            variant="outline" size="sm"
                            onClick={() => openPdf(`${API}/dimissioni/${record.id}/mandato-pdf`, 'mandato.pdf')}
                            title="Mandato Patronato + Consenso"
                          >
                            <FileText className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="outline" size="sm"
                            onClick={() => openPdf(`${API}/dimissioni/${record.id}/lettera-pdf`, 'lettera.pdf')}
                            title="Lettera Dimissioni"
                          >
                            <Mail className="w-3.5 h-3.5" />
                          </Button>
                          {record.delega_filename && (
                            <Button
                              variant="outline" size="sm"
                              onClick={() => openPdf(`${API}/dimissioni/${record.id}/delega-pdf`, 'delega.pdf')}
                              title="Delega trasmissione modulo"
                            >
                              <Download className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          <Button
                            variant="outline" size="sm"
                            onClick={() => openAllDocuments(record)}
                            title="Apri tutti i documenti"
                          >
                            <Files className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon"><MoreHorizontal className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEdit(record)}>
                              <Pencil className="w-4 h-4 mr-2" />Modifica
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleDelete(record)} className="text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" />Elimina
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
                  <p className="text-sm text-slate-500">
                    Visualizzando {startIndex + 1}-{Math.min(endIndex, totalItems)} di {totalItems} pratiche
                  </p>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-slate-600">Pagina {currentPage} di {totalPages}</span>
                    <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRecord ? 'Modifica Pratica' : 'Nuova Pratica Dimissioni'}</DialogTitle>
            <DialogDescription>
              I dati anagrafici del lavoratore (nascita, residenza, cittadinanza non inclusa) vengono presi
              automaticamente dall'anagrafica cliente per compilare Mandato Patronato e Lettera Dimissioni.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5 mt-2">
            <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2 text-violet-800 font-medium text-sm">
                <Sparkles className="w-4 h-4" />
                Estrazione automatica dati (facoltativa)
              </div>
              <p className="text-xs text-violet-700">
                Carica il documento d'identità e/o il Modulo Recesso: i campi sotto si
                compilano da soli (puoi comunque correggerli a mano).
              </p>
              <div className="space-y-1.5">
                <Label className="text-xs text-violet-700">Tipo documento d'identità che stai per caricare</Label>
                <Select value={formData.doc_tipo} onValueChange={(v) => setFormData({ ...formData, doc_tipo: v })}>
                  <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DOC_TIPO_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className={`flex items-center justify-center gap-2 border border-dashed border-violet-300 rounded-lg py-2.5 text-sm text-violet-700 hover:bg-violet-100 cursor-pointer ${extractingDoc ? 'opacity-60 pointer-events-none' : ''}`}>
                  {extractingDoc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Carica {formData.doc_tipo}
                  <input
                    type="file"
                    accept="application/pdf,image/*"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      handleExtractIdentityDoc(f);
                      e.target.value = '';
                    }}
                  />
                </label>
                <label className={`flex items-center justify-center gap-2 border border-dashed border-violet-300 rounded-lg py-2.5 text-sm text-violet-700 hover:bg-violet-100 cursor-pointer ${extractingModulo ? 'opacity-60 pointer-events-none' : ''}`}>
                  {extractingModulo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Modulo Recesso Rapporto di Lavoro
                  <input
                    type="file"
                    accept="application/pdf,image/*"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      handleExtractModuloRecesso(f);
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
            </div>

            <ClientCompletionBanner
              client={incompleteClient}
              onSaved={(updated) => {
                setIncompleteClient(updated);
                setClients(prev => prev.map(c => c.id === updated.id ? updated : c));
              }}
            />

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Lavoratore</Label>
                {!editingRecord && (
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-xs gap-1"
                    onClick={() => setNewClientMode(m => !m)}
                  >
                    <UserPlus className="w-3.5 h-3.5" />
                    {newClientMode ? 'Seleziona cliente esistente' : 'Nuovo cliente'}
                  </Button>
                )}
              </div>

              {!newClientMode ? (
                <div className="relative">
                  {/* Ricerca lavoratore come in Canone RAI: un solo campo, elenco a tendina solo mentre si cerca */}
                  <Input
                    placeholder="Cerca lavoratore per nome o codice fiscale..."
                    value={clientSearch}
                    onChange={(e) => { setClientSearch(e.target.value); setShowClientDropdown(true); }}
                    onFocus={() => setShowClientDropdown(true)}
                    onBlur={() => setTimeout(() => setShowClientDropdown(false), 200)}
                    disabled={!!editingRecord}
                    data-testid="dimissione-client-search"
                  />
                  {showClientDropdown && !editingRecord && (
                    <div className="absolute z-50 top-full left-0 right-0 mt-1 border rounded-lg max-h-48 overflow-y-auto bg-white shadow-lg">
                      {filteredClients.length === 0 ? (
                        <div className="p-3 text-center text-slate-500 text-sm">Nessun cliente trovato</div>
                      ) : (
                        filteredClients.slice(0, 10).map((c) => (
                          <div
                            key={c.id}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setFormData(prev => ({ ...prev, client_id: c.id }));
                              setClientSearch(`${c.last_name} ${c.first_name}`);
                              setShowClientDropdown(false);
                            }}
                            className={`p-3 cursor-pointer hover:bg-slate-50 border-b last:border-b-0 ${formData.client_id === c.id ? 'bg-blue-50 border-blue-200' : ''}`}
                          >
                            <div className="font-medium">{c.last_name} {c.first_name}</div>
                            <div className="text-sm text-slate-500 font-mono">{c.fiscal_code}</div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                  {formData.client_id && (
                    <p className="text-sm text-green-600 mt-2">Lavoratore selezionato</p>
                  )}
                </div>
              ) : (
                <div className="border rounded-lg p-4 space-y-3 bg-slate-50">
                  <p className="text-xs text-slate-500">
                    Il cliente non è ancora in anagrafica: compila i dati per registrarlo — verrà creato
                    al salvataggio della pratica.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">Cognome</Label>
                      <Input value={newClientData.last_name}
                        onChange={(e) => setNewClientData({ ...newClientData, last_name: e.target.value.toUpperCase() })} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">Nome</Label>
                      <Input value={newClientData.first_name}
                        onChange={(e) => setNewClientData({ ...newClientData, first_name: e.target.value.toUpperCase() })} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">Sesso</Label>
                      <Select value={newClientData.gender} onValueChange={(v) => setNewClientData({ ...newClientData, gender: v })}>
                        <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="M">Maschio</SelectItem>
                          <SelectItem value="F">Femmina</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">Data di nascita</Label>
                      <DateInput value={newClientData.birth_date}
                        onChange={(val) => setNewClientData({ ...newClientData, birth_date: val })}
                        placeholder="gg/mm/aaaa" />
                    </div>
                  </div>

                  <div className="p-3 bg-white border rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs text-slate-500">Nato/a all'estero</Label>
                      <Switch
                        checked={newClientData.birth_abroad}
                        onCheckedChange={(checked) => setNewClientData({
                          ...newClientData, birth_abroad: checked, birth_comune: '', birth_provincia: '', birth_country: checked ? 'Cina' : '',
                        })}
                      />
                    </div>
                    {!newClientData.birth_abroad ? (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-xs text-slate-500">Comune di nascita</Label>
                          <Input value={newClientData.birth_comune}
                            onChange={(e) => setNewClientData({ ...newClientData, birth_comune: e.target.value.toUpperCase() })} />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs text-slate-500">Provincia (sigla)</Label>
                          <Input value={newClientData.birth_provincia} maxLength={2} className="uppercase"
                            onChange={(e) => setNewClientData({ ...newClientData, birth_provincia: e.target.value.toUpperCase() })} />
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-500">Nazione di nascita</Label>
                        <Select value={newClientData.birth_country} onValueChange={(v) => setNewClientData({ ...newClientData, birth_country: v })}>
                          <SelectTrigger><SelectValue placeholder="Seleziona nazione" /></SelectTrigger>
                          <SelectContent className="max-h-60">
                            {NAZIONI.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-500">Codice fiscale</Label>
                    <div className="flex gap-2">
                      <Input value={newClientData.fiscal_code} className="uppercase"
                        onChange={(e) => setNewClientData({ ...newClientData, fiscal_code: e.target.value.toUpperCase() })} />
                      <Button type="button" variant="outline" size="sm" onClick={handleCalcolaCfDaDati}>Calcola CF</Button>
                      <Button type="button" variant="outline" size="sm" onClick={handleCalcolaCfNewClient} title="Calcola sesso e data di nascita dal CF">
                        <Calculator className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">Indirizzo <span className="text-blue-600">(ricerca Google Maps)</span></Label>
                      {GOOGLE_MAPS_API_KEY ? (
                        <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                          <AddressAutocomplete
                            value={newClientData.address}
                            defaultValue={newClientData.address}
                            placeholder="es. Via Roma, Piazza Duomo..."
                            onPlaceSelect={(d) => setNewClientData(prev => ({
                              ...prev,
                              address: (d.address || prev.address || '').toUpperCase(),
                              address_number: (d.address_number || prev.address_number || '').toUpperCase(),
                              city: (d.city || prev.city || '').toUpperCase(),
                              city_provincia: (d.city_provincia || prev.city_provincia || '').toUpperCase(),
                              postal_code: d.postal_code || prev.postal_code,
                            }))}
                          />
                        </APIProvider>
                      ) : (
                        <Input value={newClientData.address}
                          onChange={(e) => setNewClientData({ ...newClientData, address: e.target.value.toUpperCase() })} />
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">Numero civico</Label>
                      <Input value={newClientData.address_number}
                        onChange={(e) => setNewClientData({ ...newClientData, address_number: e.target.value.toUpperCase() })} />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1.5 col-span-2">
                      <Label className="text-xs text-slate-500">Città di residenza</Label>
                      <Input value={newClientData.city}
                        onChange={(e) => setNewClientData({ ...newClientData, city: e.target.value.toUpperCase() })} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">Provincia</Label>
                      <Input value={newClientData.city_provincia} maxLength={2} className="uppercase"
                        onChange={(e) => setNewClientData({ ...newClientData, city_provincia: e.target.value.toUpperCase() })} />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">CAP</Label>
                      <Input value={newClientData.postal_code}
                        onChange={(e) => setNewClientData({ ...newClientData, postal_code: e.target.value })} />
                    </div>
                    <div className="space-y-1.5 col-span-2">
                      <Label className="text-xs text-slate-500">Cellulare</Label>
                      <Input value={newClientData.phone}
                        onChange={(e) => setNewClientData({ ...newClientData, phone: e.target.value })} />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-500">Email</Label>
                    <Input type="email" value={newClientData.email}
                      onChange={(e) => setNewClientData({ ...newClientData, email: e.target.value.toUpperCase() })} />
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Documento d'identità</Label>
                <Select value={formData.doc_tipo} onValueChange={(v) => setFormData({ ...formData, doc_tipo: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DOC_TIPO_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Cittadinanza</Label>
                <Input value={formData.cittadinanza} onChange={(e) => setFormData({ ...formData, cittadinanza: e.target.value.toUpperCase() })} />
              </div>
              <div className="space-y-2">
                <Label>Numero documento</Label>
                <Input value={formData.doc_numero} onChange={(e) => setFormData({ ...formData, doc_numero: e.target.value.toUpperCase() })} />
              </div>
              <div className="space-y-2">
                <Label>Rilasciato da</Label>
                <Input value={formData.doc_ente} onChange={(e) => setFormData({ ...formData, doc_ente: e.target.value.toUpperCase() })} />
              </div>
            </div>

            <div className="border-t pt-4 space-y-3">
              <Label className="text-sm font-semibold text-slate-700">Datore di lavoro</Label>
              <div className="relative">
                <Input
                  placeholder="Cerca datore già registrato per denominazione o CF..."
                  value={datoreSearchTerm}
                  onChange={(e) => handleDatoreSearch(e.target.value)}
                  onFocus={() => setShowDatoreDropdown(true)}
                  onBlur={() => setTimeout(() => setShowDatoreDropdown(false), 200)}
                />
                {showDatoreDropdown && datoreSearchTerm.trim() && (
                  <div className="absolute z-50 top-full left-0 right-0 mt-1 border rounded-lg max-h-48 overflow-y-auto bg-white shadow-lg">
                    {datoreSearchResults.length === 0 ? (
                      <div className="p-3 text-center text-slate-500 text-sm">Nessun datore trovato — compila i campi a mano</div>
                    ) : (
                      datoreSearchResults.map((d) => (
                        <div
                          key={d.cf}
                          onMouseDown={(e) => { e.preventDefault(); selectDatoreFromRegistry(d); }}
                          className="p-3 cursor-pointer hover:bg-slate-50 border-b last:border-b-0"
                        >
                          <div className="font-medium">{d.denominazione}</div>
                          <div className="text-sm text-slate-500 font-mono">{d.cf}</div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2">
                  <Label className="text-xs text-slate-500">Denominazione</Label>
                  <Input value={formData.datore.denominazione}
                    onChange={(e) => setFormData({ ...formData, datore: { ...formData.datore, denominazione: e.target.value.toUpperCase() } })} />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Codice fiscale</Label>
                  <Input value={formData.datore.cf}
                    onChange={(e) => setFormData({ ...formData, datore: { ...formData.datore, cf: e.target.value.toUpperCase() } })} />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">CAP sede lavoro</Label>
                  <Input value={formData.datore.cap}
                    onChange={(e) => setFormData({ ...formData, datore: { ...formData.datore, cap: e.target.value } })} />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label className="text-xs text-slate-500">Indirizzo sede lavoro <span className="text-blue-600">(ricerca Google Maps)</span></Label>
                  {GOOGLE_MAPS_API_KEY ? (
                    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                      <AddressAutocomplete
                        value={formData.datore.indirizzo}
                        defaultValue={formData.datore.indirizzo}
                        placeholder="es. Via Roma, Piazza Duomo..."
                        onPlaceSelect={(d) => setFormData(prev => ({
                          ...prev,
                          datore: {
                            ...prev.datore,
                            indirizzo: `${d.address || ''}${d.address_number ? ` ${d.address_number}` : ''}`.toUpperCase().trim() || prev.datore.indirizzo,
                            comune: (d.city || prev.datore.comune || '').toUpperCase(),
                            cap: d.postal_code || prev.datore.cap,
                          },
                        }))}
                      />
                    </APIProvider>
                  ) : (
                    <Input value={formData.datore.indirizzo}
                      onChange={(e) => setFormData({ ...formData, datore: { ...formData.datore, indirizzo: e.target.value.toUpperCase() } })} />
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Comune sede lavoro</Label>
                  <Input value={formData.datore.comune}
                    onChange={(e) => setFormData({ ...formData, datore: { ...formData.datore, comune: e.target.value.toUpperCase() } })} />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Comune sede legale</Label>
                  <Input value={formData.datore.comune_legale}
                    onChange={(e) => setFormData({ ...formData, datore: { ...formData.datore, comune_legale: e.target.value.toUpperCase() } })} />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Email</Label>
                  <Input type="email" value={formData.datore.email}
                    onChange={(e) => setFormData({ ...formData, datore: { ...formData.datore, email: e.target.value.toUpperCase() } })} />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">PEC</Label>
                  <Input value={formData.datore.pec}
                    onChange={(e) => setFormData({ ...formData, datore: { ...formData.datore, pec: e.target.value.toUpperCase() } })} />
                </div>
              </div>
            </div>

            <div className="border-t pt-4 grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data inizio rapporto</Label>
                <Input type="date" value={formData.data_inizio} onChange={(e) => setFormData({ ...formData, data_inizio: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Tipo contratto</Label>
                <Select value={formData.tipo_contratto} onValueChange={(v) => setFormData({ ...formData, tipo_contratto: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPO_CONTRATTO_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Data decorrenza recesso</Label>
                <Input type="date" value={formData.data_decorrenza} onChange={(e) => setFormData({ ...formData, data_decorrenza: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Tipo comunicazione</Label>
                <Select value={formData.tipo_comunicazione} onValueChange={(v) => setFormData({ ...formData, tipo_comunicazione: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPO_COMUNICAZIONE_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border-t pt-4 grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Operatore (firma Mandato)</Label>
                <Select value={formData.operatore} onValueChange={(v) => setFormData({ ...formData, operatore: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {OPERATORE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Codice identificativo modulo</Label>
                <Input
                  placeholder="Dopo l'invio sul portale del Ministero"
                  value={formData.codice_modulo}
                  onChange={(e) => setFormData({ ...formData, codice_modulo: e.target.value.toUpperCase() })}
                />
              </div>
              <div className="space-y-2">
                <Label>Data trasmissione</Label>
                <Input type="date" value={formData.data_trasmissione} onChange={(e) => setFormData({ ...formData, data_trasmissione: e.target.value })} />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
              <Button type="submit" disabled={saving || creatingClient}>
                {creatingClient ? 'Registrazione cliente...' : saving ? 'Salvataggio...' : 'Salva'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dimissioni;
